import { z } from "zod";
import { GameVersion } from "./version.js";
import { Hex, Hex32 } from "./ids.js";

/** One variant per GA instruction (plan §5.4). Byte arrays are lowercase hex. */
const Counts10U8 = z.array(z.number().int().min(0).max(255)).length(10);
const Counts10U16 = z.array(z.number().int().min(0).max(65535)).length(10);

export const ChainOpPayload = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("sync_begin"), prevHash: Hex32, newHash: Hex32, badges: z.number().int().min(0).max(255) }),
  z.object({ kind: z.literal("sync_money"), delta: z.number().int().min(-999_999).max(999_999) }),
  z.object({ kind: z.literal("sync_items"), found: Counts10U8, consumed: Counts10U16 }),
  z.object({
    kind: z.literal("mint_mon"),
    fingerprint: Hex32,
    originPlayer: z.string(),
    boxStruct: Hex, // 33-byte box struct
    nickname: Hex, // 11 bytes, game text encoding
    otName: Hex, // 11 bytes
  }),
  z.object({ kind: z.literal("update_mon"), asset: z.string(), fingerprint: Hex32, boxStruct: Hex, nickname: Hex }),
  // playerA/playerB are WALLETS; chain-client derives the Player PDAs.
  z.object({
    kind: z.literal("trade_mons"),
    saveSeqA: z.number().int(),
    saveSeqB: z.number().int(),
    playerA: z.string(),
    playerB: z.string(),
    assetA: z.string(),
    assetB: z.string(),
    fingerprintA: Hex32,
    fingerprintB: Hex32,
    boxStructA: Hex,
    boxStructB: Hex,
  }),
  z.object({ kind: z.literal("mark_release"), asset: z.string(), fingerprint: Hex32 }),
  // Day Care takings (§5.9): the increase of the save's paid counter, moved from the reserve to the treasury.
  z.object({ kind: z.literal("charge_day_care"), rcd: z.number().int().min(1).max(99_999_999) }),
  // Sweeps the player's whole ₽ deposit address into game_balance; `amount` is what the planner saw.
  z.object({ kind: z.literal("credit_deposit_address"), amount: z.string().regex(/^\d+$/) }),
  // Pays waiting mints from the player's wallet, within the allowance it approved to the deposit PDA (§15.5.5).
  z.object({ kind: z.literal("pull_wallet_rcd"), amount: z.string().regex(/^[1-9]\d{0,9}$/) }),
  z.object({ kind: z.literal("sync_commit"), autosave: z.boolean() }),
  z.object({ kind: z.literal("settle_credit") }),
  z.object({ kind: z.literal("sweep_fees") }),
  z.object({ kind: z.literal("release_mon"), asset: z.string(), fingerprint: Hex32, ownerWallet: z.string() }),
  z.object({ kind: z.literal("cancel_release"), asset: z.string(), fingerprint: Hex32 }),
  z.object({ kind: z.literal("close_orphan_mon_state"), asset: z.string(), fingerprint: Hex32, recipient: z.string() }),
  z.object({ kind: z.literal("taint_generation") }),
  z.object({ kind: z.literal("start_new_game"), trainerId: z.number().int().min(0).max(65535), trainerName: Hex, gameVersion: GameVersion }),
  z.object({ kind: z.literal("migrate_item_bag"), oldWallet: z.string(), newWallet: z.string() }),
  z.object({ kind: z.literal("migrate_mon"), asset: z.string(), fingerprint: Hex32, oldWallet: z.string(), newWallet: z.string() }),
  z.object({ kind: z.literal("migrate_player_finish"), oldWallet: z.string(), newWallet: z.string() }),
  // wagerId: u64 decimal (wager PDA seed). playerA, playerB and winner are Player PDAs, not wallets.
  z.object({ kind: z.literal("settle_wager"), wagerId: z.string().regex(/^\d{1,20}$/), playerA: z.string(), playerB: z.string(), winner: z.string() }),
  // Draws and aborted sessions refund both stakes; GA signs. playerA/playerB are Player PDAs.
  z.object({ kind: z.literal("cancel_wager"), wagerId: z.string().regex(/^\d{1,20}$/), playerA: z.string(), playerB: z.string() }),
  z.object({ kind: z.literal("set_paused"), paused: z.literal(true) }),
  z.object({ kind: z.literal("set_claims_paused"), paused: z.literal(true) }),
]);
export type ChainOpPayload = z.infer<typeof ChainOpPayload>;
export type ChainOpKind = ChainOpPayload["kind"];

export const ChainOpStatus = z.enum(["pending", "submitted", "confirmed", "finalized", "failed", "quarantined", "skipped"]);
export type ChainOpStatus = z.infer<typeof ChainOpStatus>;

/** `"{player}:{save_seq}:{kind}:{subject}"` (§10.3). */
export function idempotencyKey(player: string, saveSeq: number | null, kind: ChainOpKind, subject = ""): string {
  return `${player}:${saveSeq ?? "-"}:${kind}:${subject}`;
}

/** §16.1 dependency order within one checkpoint. */
export const OP_ORDER: Record<ChainOpKind, number> = {
  start_new_game: 0,
  credit_deposit_address: 0.5,
  sync_begin: 1,
  sync_money: 2,
  pull_wallet_rcd: 2.5,
  sync_items: 3,
  mint_mon: 4,
  update_mon: 5,
  charge_day_care: 5.5,
  trade_mons: 6,
  mark_release: 7,
  sync_commit: 8,
  taint_generation: 9,
  settle_credit: 10,
  sweep_fees: 10,
  release_mon: 10,
  cancel_release: 10,
  close_orphan_mon_state: 10,
  migrate_item_bag: 11,
  migrate_mon: 12,
  migrate_player_finish: 13,
  settle_wager: 10,
  cancel_wager: 10,
  set_paused: 0,
  set_claims_paused: 0,
};
